import { useGrid } from '@/queries/grids/useGrid'
import { resetGrid, setGrid } from '@/redux/store/slices/grid/gridSlice'
import { useAppDispatch } from '@redux/hooks/useAppState'
import { useStytchOrganization } from '@stytch/react/b2b'
import { generateGridVersionPayload } from 'honeygrid-types'
import { useEffect, useLayoutEffect, useRef } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import type { FC, PropsWithChildren } from 'react'

export const GridReduxHandler: FC<PropsWithChildren> = ({ children }) => {
	const { workspaceId = '', '*': gridIdFromUrl = '' } = useParams()
	const { organization } = useStytchOrganization()
	const [searchParams] = useSearchParams()
	const selectedGridId = searchParams.get('gridId') || ''
	// When on the /add route, gridIdFromUrl will be "add", so prioritize query param
	const gridId = gridIdFromUrl === 'add' ? selectedGridId : gridIdFromUrl || selectedGridId
	const isAddRoute = gridIdFromUrl === 'add'
	const { data: grid, isError } = useGrid({
		workspaceId: organization?.organization_id,
		gridId,
		include: 'currentVersion',
		enabled: !isAddRoute && !!gridId, // Don't fetch when adding new grid
	})
	const workspaceIdRef = useRef(workspaceId)
	const dispatch = useAppDispatch()

	useLayoutEffect(() => {
		if (grid) {
			const gridWithVersion = {
				...grid,
				currentVersion: grid.currentVersion ?? generateGridVersionPayload({ empty: true }),
			}
			dispatch(setGrid(gridWithVersion))
		} else if (isError || !gridId || gridId === 'add') {
			// Only reset if there's an error or no gridId
			dispatch(resetGrid())
		}
	}, [dispatch, grid, isError, gridId])

	// When changing workspaces, reset grid state
	useEffect(() => {
		if (workspaceIdRef.current !== workspaceId) {
			dispatch(resetGrid())
			workspaceIdRef.current = workspaceId
		}
	}, [dispatch, workspaceId])

	return <>{children}</>
}
